import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
// eslint-disable-next-line import/no-unresolved
import { v4 as uuidv4 } from "uuid";
import PropTypes from "prop-types";
import usePrevious from "src/util/usePrevious";
import { useAuth0 } from "src/contexts/auth0Provider";
import { useAISidebar } from "src/hooks/useAISidebar";
import { EventBus, EVENTS } from "src/services/eventEmitter";
import {
  useApolloSettings,
  useTenantId,
} from "src/contexts/global/ApolloSettingsContext";
import {
  useWorkspaceFeatureFlags,
  useWorkspace,
} from "src/contexts/global/WorkspaceContext";

import { AGENTCORE_NAMES, SOURCE_TYPES } from "../consts";
import streamAIResponse from "../utils/streamAIResponse";
import { AIChatContext } from "./AIChatContext";
import useGetAIChat from "./hooks/useGetAIChat";

export const AIChatProvider = ({ children, sourceId }) => {
  const authState = useAuth0();
  const workspace = useWorkspace();
  const { id: workspaceId } = workspace;
  const [question, setQuestion] = useState("");

  const [complete, setComplete] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [error, setError] = useState(null);
  const [userHasScrolledUp, setUserHasScrolledUp] = useState(false);
  const { apiUrl } = useApolloSettings();
  const tenantId = useTenantId();

  const {
    chatId,
    chats,
    loading: chatsLoading,
    onStartNewChat,
    onSelectChat,
    currentChat,
    onUpsertAIChatMessage,
    updateChatOptimistically,
    onSubmitMessageFeedback,
  } = useGetAIChat();

  const messages = useMemo(() => currentChat?.messages || [], [currentChat]);

  /** @type {{ versionId: string|null, updateSidebarState: Function }} AI sidebar state and controls */
  const { versionId } = useAISidebar();

  /** @type {{ enableAIChat: boolean }} Feature flags from workspace context */
  const { enableAIChat } = useWorkspaceFeatureFlags();
  /**
   * Determines if AI chat is enabled based on feature flags and source
   * @type {boolean}
   * @memoized
   */
  const aiEnabled = useMemo(
    () => enableAIChat && sourceId === SOURCE_TYPES.chat_q_and_a,
    [enableAIChat, sourceId]
  );

  const previousMessages = usePrevious(messages);

  /**
   * Helper function to clear messages and reset state
   * @returns {void}
   */
  const onSessionClear = useCallback(() => {
    // Reset current question and state
    setUserHasScrolledUp(false);
    setError(null);
    setQuestion("");
    setComplete(true);
  }, [setError]);

  /** @type {React.MutableRefObject<string|null>} Previous version ID for change detection */
  const previousVersionId = useRef(versionId);

  /**
   * Effect for managing chat session lifecycle based on versionId changes
   * @effect
   * @description Clears session when versionId changes from value to null,
   * sets loading state for new sessions, and tracks versionId changes
   * @param {Function} clearChatSession - Session clearing function
   * @param {string|null} versionId - Current version identifier
   * @param {number} messages.length - Number of current messages
   * @param {Function} setComplete - Completion state setter
   */
  useEffect(() => {
    // Clear session only when versionId changes from a value to null
    if (previousVersionId.current && !versionId) {
      onSessionClear();
    }

    previousVersionId.current = versionId;
  }, [onSessionClear, versionId, messages.length, setComplete]);

  const invokeAIResponse = useCallback(
    async (newQuestion, isRetry = false) => {
      try {
        setQuestion("");
        setIsSubmitting(true);
        setComplete(false);
        setIsThinking(true);

        const userMessage = {
          id: uuidv4(),
          role: "user",
          content: newQuestion,
          status: "done",
          tools: [],
        };

        if (!isRetry && newQuestion) {
          await onUpsertAIChatMessage(userMessage);
        }

        const token = await authState.getTokenSilently();

        streamAIResponse(
          {
            messages: [...messages, userMessage]
              .filter((m) => m.content)
              .map((m) => ({
                role: m.role,
                content: [{ text: m.content }],
              })),
            aiChatId: chatId,
          },
          {
            tenantId,
            apiUrl,
            agentName: AGENTCORE_NAMES.ASK_TEAMFORM,
            headers: {
              Authorization: `Bearer ${token}`,
              "x-workspace-id": workspaceId,
            },

            onError: (e) => {
              setError(e);
              setIsThinking(false);
              setComplete(true);
              setIsSubmitting(false);
            },

            onMessageChunk: (chunkEvent) => {
              if (chunkEvent?.message) {
                // Use the formatted HTML content from the top-level 'content' field
                // instead of the raw markdown in message.content
                const messageWithFormattedContent = {
                  ...chunkEvent.message,
                  content: chunkEvent.content || chunkEvent.message.content,
                };
                updateChatOptimistically(messageWithFormattedContent);
                setIsThinking(false);
              }
            },
            onDone: () => {
              setComplete(true);
              setIsSubmitting(false);
              setIsThinking(false);
            },
            onMessage: (messageEvent) => {
              if (messageEvent?.final_message) {
                updateChatOptimistically(messageEvent?.final_message);
              }

              if (
                messageEvent?.final_message?.content?.includes("<dashboard")
              ) {
                const dashboardId = messageEvent?.final_message?.content?.match(
                  /<dashboard[^>]*\bid="([^"]+)"/
                )?.[1];
                const title = messageEvent?.final_message?.content?.match(
                  /<dashboard[^>]*\btitle="([^"]+)"/
                )?.[1];

                if (dashboardId || title) {
                  EventBus.emit(EVENTS.AI_DASHBOARD_UPDATED, {
                    dashboardId,
                    title,
                  });
                }
              }
            },
          }
        );
      } catch (e) {
        setError(e);
        setIsSubmitting(false);
        setComplete(true);
        setIsThinking(false);
      }
    },
    [
      authState,
      messages,
      chatId,
      tenantId,
      apiUrl,
      workspaceId,
      onUpsertAIChatMessage,
      updateChatOptimistically,
    ]
  );

  const onSendQuestion = useCallback(
    () => invokeAIResponse(question),
    [invokeAIResponse, question]
  );

  const onRetry = useCallback(() => {
    invokeAIResponse("", true);
  }, [invokeAIResponse]);

  const showLoader = useMemo(() => {
    return isSubmitting;
  }, [isSubmitting]);

  const values = useMemo(
    () => ({
      chatId,
      messages,
      previousMessages,
      complete,
      hasError: !!error,
      setUserHasScrolledUp,
      userHasScrolledUp,
      question,
      setQuestion,
      onSessionClear,
      onSendQuestion,
      showLoader,
      onRetry,
      error,
      setError,
      onStartNewChat,
      chats,
      chatsLoading,
      onSelectChat,
      isThinking,
      onSubmitMessageFeedback,
    }),
    [
      chatId,
      messages,
      previousMessages,
      complete,
      setUserHasScrolledUp,
      userHasScrolledUp,
      question,
      setQuestion,
      onSessionClear,
      onSendQuestion,
      showLoader,
      onRetry,
      error,
      setError,
      onStartNewChat,
      chats,
      chatsLoading,
      onSelectChat,
      isThinking,
      onSubmitMessageFeedback,
    ]
  );

  if (!aiEnabled) {
    return null;
  }

  return (
    <AIChatContext.Provider value={values}>{children}</AIChatContext.Provider>
  );
};

AIChatProvider.propTypes = {
  children: PropTypes.node.isRequired,
  sourceId: PropTypes.string.isRequired,
  // subjectId: PropTypes.string.isRequired,
};
